import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Package } from "lucide-react";

export default function PaymentSuccess({ user, logout }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentDetails(data);
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-success-page">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <Card className="overflow-hidden">
            <CardContent className="p-8 text-center">
              {status === "checking" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Procesando pago...
                  </h1>
                  <p className="text-muted-foreground">
                    Estamos verificando tu pago. Por favor, espera un momento.
                  </p>
                </>
              )}

              {status === "success" && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2 text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    ¡Pago completado!
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Te has unido exitosamente a la compra grupal. Recibirás actualizaciones sobre el estado del paquete.
                  </p>
                  
                  {paymentDetails && (
                    <div className="p-4 bg-gray-50 rounded-lg mb-6 text-left">
                      <p className="text-sm text-muted-foreground">Total pagado</p>
                      <p className="text-xl font-bold">
                        {(paymentDetails.amount_total / 100).toFixed(2)}€
                      </p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Link to="/dashboard">
                      <Button className="w-full" data-testid="go-to-dashboard-btn">
                        <Package className="h-4 w-4 mr-2" />
                        Ver mis compras
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="w-full">
                        Seguir comprando
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {(status === "failed" || status === "expired" || status === "timeout") && (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2 text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {status === "timeout" ? "Verificación tardando" : "Pago no completado"}
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    {status === "timeout" 
                      ? "La verificación del pago está tardando más de lo esperado. Por favor, revisa tu email para confirmar el estado."
                      : "Hubo un problema con el pago. Por favor, inténtalo de nuevo."}
                  </p>
                  
                  <div className="space-y-3">
                    <Link to="/">
                      <Button className="w-full" data-testid="back-to-offers-btn">
                        Volver a ofertas
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button variant="outline" className="w-full">
                        Ver mis compras
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
